import { GAME_CONFIG } from '../config/game';

export interface Multiplier {
  id: string;
  type: number;
  basePrice: number;
  currentPrice: number;
  owned: number;
  maxOwned: number;
  description: string;
  color: string;
  unlockLevel: number;
  effect: number;
}

class MultiplierService {
  private calculatePrice(basePrice: number, owned: number): number {
    return Math.floor(basePrice * Math.pow(GAME_CONFIG.priceIncreaseFactor, owned));
  }

  getMultipliers(currentLevel: number, ownedMultipliers: { [key: string]: number }): Multiplier[] {
    return GAME_CONFIG.multipliers.map(m => ({
      type: m.type,
      basePrice: m.basePrice,
      maxOwned: m.maxOwned,
      description: m.description,
      color: m.color,
      unlockLevel: m.unlockLevel,
      effect: m.type,
      id: `multiplier_${m.type}`,
      owned: ownedMultipliers[`multiplier_${m.type}`] || 0,
      currentPrice: this.calculatePrice(
        m.basePrice,
        ownedMultipliers[`multiplier_${m.type}`] || 0
      )
    }));
  }

  calculateTotalMultiplier(ownedMultipliers: { [key: string]: number }): number {
    let totalMultiplier = 1;
    
    GAME_CONFIG.multipliers.forEach(m => {
      const owned = ownedMultipliers[`multiplier_${m.type}`] || 0;
      if (owned > 0) {
        totalMultiplier *= m.type;
      }
    });
    
    return totalMultiplier;
  }

  canBuyMultiplier(
    multiplier: Multiplier,
    balance: number,
    currentLevel: number
  ): { canBuy: boolean; reason?: string } {
    if (currentLevel < multiplier.unlockLevel) {
      return { canBuy: false, reason: `Débloqué au niveau ${multiplier.unlockLevel}` };
    }

    if (multiplier.owned >= multiplier.maxOwned) {
      return { canBuy: false, reason: 'Limite maximale atteinte' };
    }

    if (balance < multiplier.currentPrice) {
      return { canBuy: false, reason: 'Pas assez de coins' };
    }

    return { canBuy: true };
  }

  getNextMultiplierUnlock(currentLevel: number): { level: number; type: number } | null {
    const nextMultiplier = GAME_CONFIG.multipliers
      .filter(m => m.unlockLevel > currentLevel)
      .sort((a, b) => a.unlockLevel - b.unlockLevel)[0];

    return nextMultiplier 
      ? { level: nextMultiplier.unlockLevel, type: nextMultiplier.type }
      : null;
  }

  getMultiplierEfficiency(multiplier: Multiplier): number {
    return multiplier.type / multiplier.currentPrice;
  }

  getRecommendedMultiplier(
    multipliers: Multiplier[],
    balance: number,
    currentLevel: number
  ): Multiplier | null {
    return multipliers
      .filter(m => this.canBuyMultiplier(m, balance, currentLevel).canBuy)
      .sort((a, b) => this.getMultiplierEfficiency(b) - this.getMultiplierEfficiency(a))[0] || null;
  }
}

export const multiplierService = new MultiplierService();